import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { ChangesDetection } from "../../../../change-detection";
import { I_GroupedFieldConfig } from "../api/grouped-field-config.response";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { E_SmartDisplayGraphResult } from "../../../../../enums/smart-display/graphs/graph-result.enum";

export class C_GroupedFieldConfigList extends ChangesDetection<T_GroupFieldListChange> {

  private list: Map<string, C_GroupedFieldConfig> = new Map();
  private cachedSubs = new Map<string, Observable<C_GroupedFieldConfig | undefined>>();

  constructor() {
    super();
  }
  
  setGroupedField(gField: I_GroupedFieldConfig) {
    const gName = gField.name;
    if (!gName) return;

    if (this.list.has(gName)) {
      const currentGroupedObs = this.list.get(gName)!;
      currentGroupedObs.initialize(gField);
      this.forceUpdateDetection({
        action: E_ChangeAction.Update,
        groupName: gName,
        data: currentGroupedObs
      });
    } else {
      const newGroupedObs = new C_GroupedFieldConfig(gField);
      this.list.set(gName, newGroupedObs);
      this.forceUpdateDetection({
        action: E_ChangeAction.Add,
        groupName: gName,
        data: newGroupedObs,
      });
    }
  }

  setGroupedFieldListBulk(gFieldList: I_GroupedFieldConfig[]) {
    if (!gFieldList.length) return;
    const affected = new Set<string>();

    gFieldList.forEach(gField => {
      const obsName = gField.name;
      if (!obsName) return;

      affected.add(`${obsName}`);

      if (this.list.has(obsName)) {
        const currentGroupedObs = this.list.get(obsName)!;
        currentGroupedObs.initialize(gField);
      } else {
        const newGroupedObs = new C_GroupedFieldConfig(gField);
        this.list.set(obsName, newGroupedObs);
      }
    });
    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected, data: gFieldList });
  }

  getLiveGroupedField(gFieldName: string): Observable<C_GroupedFieldConfig | undefined> {
    const cacheKey = `${gFieldName}`;
    if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Permite el disparo inicial (startWith)
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.Add) return change.groupName === gFieldName;
        if (change.action === E_ChangeAction.AddBulk) return change.affected.has(cacheKey);
        return false;
      }),
      map(() => {
        return this.list.get(gFieldName);
      }),
      distinctUntilChanged(),
      switchMap(gFieldName => {
        if (!gFieldName) return of(undefined);
        // Escucha mutaciones internas de la instancia de observación agrupada
        return gFieldName.getForcedChanges().pipe(
          startWith(gFieldName),
          map(() => gFieldName)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(cacheKey, stream$);
    return stream$;
  }

  getGroupedField(name: string) {
    return this.list.get(name);
  }

  getGroupedFields(): C_GroupedFieldConfig[] {
    return Array.from(this.list.values());
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }

}

export type T_GroupFieldListChange =
  | { action: E_ChangeAction.Add; groupName: string; data: C_GroupedFieldConfig }
  | { action: E_ChangeAction.Update; groupName: string; data: C_GroupedFieldConfig }
  | { action: E_ChangeAction.AddBulk; affected: Set<string>; data: I_GroupedFieldConfig[] }
  | { action: E_ChangeAction.Reset };

export class C_GroupedFieldConfig extends ChangesDetection {
  name: string = '';
  group: string = '';
  names?: string[];
  max?: number;
  result: E_SmartDisplayGraphResult[] = [];
  regularity: string = '';
  startTimeShift: string[] = [];
  needRequest: boolean = true;
  constructor(data?: I_GroupedFieldConfig) {
    super();
    if (data) this.initialize(data);
  }

  initialize(data: I_GroupedFieldConfig) {
    this.name = data.name;
    this.group = data.group;
    this.max = data.max;
    this.result = data.result;
    this.regularity = data.regularity;
    this.startTimeShift = data.startTimeShift;
    if (this.needRequest && data.needRequest === false) this.needRequest = false;
    if (data.hasOwnProperty('names')) this.names = data.names;
    this.forceUpdateDetection();
  }

}
